import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i3 from '@angular/common';
import { DOCUMENT } from '@angular/common';
import * as i4 from '@upr-web-primer/card-promotion';
import * as i1 from '@upr-web-primer/locale';
import * as i2 from '@upr-web-primer/storage';
import * as i5 from '@upr-web-primer/tcm-service';
import { WINDOW } from '@upr-web-primer/window';
import { get } from 'lodash-es';
import { DateTime } from 'luxon';

class GdsCommonUtilityService {
    constructor(document, tridionDefaults, appDefaults, appRegExs, window, localeService, localStorage, location, cardPromotionService, tridion) {
        this.document = document;
        this.tridionDefaults = tridionDefaults;
        this.appDefaults = appDefaults;
        this.appRegExs = appRegExs;
        this.window = window;
        this.localeService = localeService;
        this.localStorage = localStorage;
        this.location = location;
        this.cardPromotionService = cardPromotionService;
        this.tridion = tridion;
        this.formatNumbers = (num) => Number(num).toLocaleString(this.locale_id);
        this.uid = () => {
            return `gds-group-${Math.random().toString(36).substring(2, 6)}`;
        };
        this.baseHref = this.location.getBaseHref();
        this.baseRedirectBypass = get(this.appDefaults, 'BASE_REDIRECT_BYPASS', false);
        this.webPath = get(this.appDefaults, 'WEB_APP_PATH', '');
        const locale = this.localeService.getFromStorage();
        this.language =
            locale.language || get(this.appDefaults, 'DEFAULT_ROUTE_COUNTRY');
        this.country =
            locale.country || get(this.appDefaults, 'DEFAULT_ROUTE_LANGUAGE');
        if (this.appDefaults.TRIDION_DYNAMIC_PATH_ENABLED) {
            this.tridion.tridionConfig$.subscribe((tridionConfig) => {
                const micrositeConfigs = get(tridionConfig, '0.sites', []);
                this.microsites = micrositeConfigs.map((siteConfig) => {
                    const site = get(siteConfig, 'site', '');
                    const siteSegment = site.split('/');
                    return siteSegment[1];
                });
            });
            this.tridion.siteConfig$.subscribe((siteConfig) => {
                if (siteConfig) {
                    this.site = get(siteConfig, 'tridion_pub', '');
                    this.siteWithLocale = [this.site, this.language, this.country].join('/');
                    this.appDirectory = get(siteConfig, 'tridion_sub_directory', undefined);
                    this.dynamicSite = get(siteConfig, 'site', '');
                }
            });
        }
        else {
            this.site = get(this.tridionDefaults, 'TRIDION_SITE');
            this.siteWithLocale = [this.site, this.language, this.country].join('/');
            this.appDirectory = get(this.appDefaults, 'TRIDION_APP_DIRECTORY');
        }
        this.locale_id = [this.language, this.country.toUpperCase()].join('-');
    }
    getAnchor(data) {
        return get(data, 'link.EmbeddedValues[0].AnchorTag.Values[0]', '').replace('#', '');
    }
    getContrast() {
        const contrast = this.localStorage.getItem('contrast') === 'true';
        this.localStorage.setItem('contrast', contrast);
        return contrast;
    }
    getImageUrl(field, imageType, fieldTemplate = true, index = 0, isXpath) {
        if (isXpath) {
            return get(field, `EmbeddedValues[${index}]['${imageType}'].XPath`);
        }
        if (fieldTemplate) {
            if (imageType === 'Multimedia') {
                return this.resolveImagePath(get(field, `LinkedComponentValues[${index}]['${imageType}'].Url`));
            }
            else {
                const value = get(field, `EmbeddedValues[${index}]['${imageType}'].LinkedComponentValues[0].Multimedia.Url`) || get(field, `EmbeddedValues[${index}]['${imageType}'].Values[0]`);
                return this.resolveImagePath(value);
            }
        }
        return this.resolveImagePath(get(field, imageType));
    }
    getHref(data, fieldTemplate = true) {
        if (fieldTemplate) {
            return this.resolveLinkUrl(get(data, 'link.EmbeddedValues[0].Component.LinkedComponentValues[0].Multimedia.Url') ||
                get(data, 'link.EmbeddedValues[0].External.Values[0]') ||
                get(data, 'link.EmbeddedValues[0].Component.LinkedComponentValues[0].ResolvedUrl') ||
                get(data, 'href.EmbeddedValues[0].External.Values[0]') ||
                get(data, 'href.EmbeddedValues[0].Component.LinkedComponentValues[0].ResolvedUrl'), get(data, 'target.KeywordValues[0].Key'));
        }
        return this.resolveLinkUrl(data, get(data, 'target.KeywordValues[0].Key'));
    }
    isModal(btnTarget, btnType) {
        const modalTypes = ['play', 'gallery'];
        return (['modal', 'fullScreenModal', 'youtube'].includes(btnTarget) ||
            modalTypes.includes(btnType));
    }
    addSpacer() {
        const gdsSpacer = this.document.createElement('gds-spacer');
        gdsSpacer.setAttribute('type', 'small');
        return gdsSpacer;
    }
    resolveImagePath(imgPath) {
        if (!imgPath || imgPath.indexOf('/' + this.siteWithLocale) !== 0) {
            return imgPath;
        }
        return imgPath.replace('/' + this.siteWithLocale, get(this.appDefaults, 'TRIDION_WEBDATA') + '/' + this.siteWithLocale);
    }
    resolveLinkUrl(link, target) {
        let contentPath = '';
        if (!link) {
            return link;
        }
        if (this.appRegExs.FORMATTED_LINK_EXP.test(link)) {
            return link;
        }
        if (this.appRegExs.INTERNAL_REGEX.test(link)) {
            link = link.replace(this.appRegExs.INTERNAL_REGEX, '');
            if (this.window.location.hostname !== 'localhost') {
                return `/web/${this.language}/${this.country}/${link
                    .replace('/index.html', '')
                    .replace('./', '')}`;
            }
            else {
                return `/${this.language}/${this.country}/${link
                    .replace('/index.html', '')
                    .replace('./', '')}`;
            }
        }
        const isInternalComponentLink = link.startsWith(`/${this.site}`);
        if (isInternalComponentLink) {
            const isRouteLink = target === 'routeLink';
            const contentPathInfo = this.getContentPathInfo(link, isRouteLink);
            contentPath = contentPathInfo.contentPath;
            link = contentPathInfo.link;
        }
        if (link.includes('/index.html') && target === 'routeLink') {
            link = link
                .replace(`${this.appDirectory}/`, '')
                .replace('/index.html', '');
        }
        if (this.appRegExs.MULTIMEDIA_REGEX.test(link)) {
            const tridion_webdata = get(this.appDefaults, 'TRIDION_WEBDATA');
            link = tridion_webdata + (link.startsWith('/') ? '' : '/') + link;
            return link;
        }
        link = link.replace('/' + this.siteWithLocale, this.siteWithLocale.replace(this.site + '/', contentPath));
        return link;
    }
    createObjectFromArray(arr) {
        const result = {};
        if (!Array.isArray(arr)) {
            return result;
        }
        arr.forEach((item) => {
            const keyName = item?.key?.Values?.[0] || item?.key;
            const value = item?.value?.Values?.[0] || item?.value;
            if (keyName && value) {
                result[keyName] = value;
            }
        });
        return result;
    }
    applicationRequestHandler(customEventData) {
        if (customEventData?.application?.toLowerCase() === 'fnbo') {
            this.cardPromotionService.buttonActionHandler(customEventData);
            return;
        }
    }
    getComponentData(el, prop = 'data') {
        const data = get(el, prop);
        if (!data) {
            return {};
        }
        return typeof data === 'string' ? JSON.parse(data) : data;
    }
    replaceHashCodes(template, variables, removeEmptyHash = true) {
        return template.replace(/##(\w+)##/gi, (match, p1) => {
            return variables[p1.toLowerCase()] ?? (removeEmptyHash ? '' : match);
        });
    }
    createTimeElement(dateObject, options) {
        if (!dateObject.isValid) {
            return '';
        }
        const datetime = get(options, 'datetime', dateObject.toISO());
        const format = get(options, 'format', '');
        const locale = get(options, 'locale', DateTime.TIME_SIMPLE);
        let text = '';
        if (format) {
            text = dateObject.toFormat(format);
        }
        else {
            text = dateObject.toLocaleString(locale);
        }
        return `<time datetime='${datetime}'>${text}</time>`;
    }
    getContentPathInfo(link, isRouteLink) {
        const webPath = this.getUrlInfo(this.webPath);
        const appPath = this.getUrlInfo(this.appDirectory);
        const appDirectoryPath = this.dynamicSite || appPath.href;
        if (this.appDirectory && link.includes(appPath.path)) {
            return {
                contentPath: isRouteLink ? '' : appDirectoryPath,
                link: link.replace(appPath.path, '/'),
            };
        }
        else if (link.includes('/account-management/')) {
            const accountPath = this.getUrlInfo(get(this.appDefaults, 'ACCOUNT_APP_PATH', ''));
            return {
                contentPath: isRouteLink && this.baseHref === accountPath.path
                    ? ''
                    : accountPath.href,
                link,
            };
        }
        else if (!this.appDirectory &&
            this.baseHref === '/' &&
            !this.baseRedirectBypass) {
            const pathSegments = this.window.location.pathname.split('/');
            const startPath = pathSegments.length > 1 ? pathSegments[1] : '';
            const formattedStartPath = this.getUrlInfo(startPath).path;
            const formattedPublication = this.formatPath(this.site);
            const hasStartPath = startPath && link.startsWith(formattedPublication + startPath);
            const locationPath = hasStartPath ? '' : formattedStartPath;
            return {
                contentPath: this.dynamicSite || locationPath,
                link,
            };
        }
        else if (!this.appDirectory &&
            isRouteLink &&
            webPath.path === this.baseHref) {
            return { contentPath: '', link };
        }
        return {
            contentPath: webPath.href,
            link,
        };
    }
    getUrlInfo(url) {
        if (!url) {
            return {
                href: '',
                path: '',
            };
        }
        if (this.appRegExs.FORMATTED_LINK_EXP.test(url)) {
            const urlInfo = new URL(url);
            const path = urlInfo.pathname + '/';
            const href = urlInfo.host === this.window.location.host
                ? path
                : urlInfo.origin + path;
            return { href, path };
        }
        const formattedPath = this.formatPath(url);
        return { href: formattedPath, path: formattedPath };
    }
    formatPath(path) {
        let formattedPath = path;
        if (!path.startsWith('/')) {
            formattedPath = '/' + formattedPath;
        }
        if (!path.endsWith('/')) {
            formattedPath = formattedPath + '/';
        }
        return formattedPath;
    }
}
GdsCommonUtilityService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsCommonUtilityService, deps: [{ token: DOCUMENT }, { token: 'TridionDefaults' }, { token: 'AppDefaults' }, { token: 'AppRegexs' }, { token: WINDOW }, { token: i1.LocaleService }, { token: i2.LocalStorageService }, { token: i3.LocationStrategy }, { token: i4.CardPromotionService }, { token: i5.TcmService }], target: i0.ɵɵFactoryTarget.Injectable });
GdsCommonUtilityService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsCommonUtilityService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsCommonUtilityService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['TridionDefaults']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppDefaults']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppRegexs']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: i1.LocaleService }, { type: i2.LocalStorageService }, { type: i3.LocationStrategy }, { type: i4.CardPromotionService }, { type: i5.TcmService }]; } });

class GdsCommonUtilityModule {
}
GdsCommonUtilityModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsCommonUtilityModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GdsCommonUtilityModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsCommonUtilityModule });
GdsCommonUtilityModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsCommonUtilityModule, providers: [GdsCommonUtilityService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsCommonUtilityModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [GdsCommonUtilityService]
                }]
        }] });

export { GdsCommonUtilityModule, GdsCommonUtilityService };
